/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 * @description ExpensesAnalyzer page container with table and graph.
 * @component PageExpensesAnalyzer
 *
 * @author Maxim Fedorov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Dmitry Okunev
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.1.0
 */

import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EChartDataKeyEnum } from '@components/common/Chart/chartTypes';
import type { IChartDataInput } from '@components/common/Chart/chartTypes';
import { SIDE_BAR_SELECT_ALL_ITEM } from '@components/common/SideBar/SideBarConstants';
import Title from '@components/common/Title/Title';
import {
    loadTableListAction,
    loadTableMeta,
    loadTableSubRow,
    resetBasketAction,
    resetBasketCount,
    sendTableFields,
    sendTableFieldsSize,
    setRangeMonthFilter,
    setTableCurrentName,
    setTableSortAction,
    switchCheckbox,
} from '@components/Table/actions/tableActions';
import { makeSelectorInitBasketList } from '@components/Table/components/footerModal/Basket/basketSelectors';
import type { IBasketItem } from '@components/Table/components/footerModal/Basket/basketTypes';
import type { ITableReducer } from '@components/Table/reducers/tableReducer';
import {
    makeSelectorTableCurrentName,
    makeSelectorTableData,
    makeSelectorTableFilter,
} from '@components/Table/selectors/tableSelectors';
import {
    MapTableNameToApiPath,
    MapTableNameToBasketApiPath,
    MapTableNameToDetailsApiPath,
    MapTableNameToListApiPath,
    MapTableNameToProxyApiPath,
} from '@components/Table/types/tableMapTypes';
import { EResetBasketType, TableNameType } from '@components/Table/types/tableTypes';
import type {
    IFieldResize,
    ILoadTableListPayload,
    ILoadTableMetaPayload,
    IResetBasketActionPayload,
    ISendTableFieldsPayload,
    ISendTableFieldsSizePayload,
    ISetRangeMonthFilterPayload,
    ISetTableSortPayload,
    ISwitchCheckboxPayload,
    ITableFooterSize,
} from '@components/Table/types/tableTypes';

import PageLocale from '../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageAnalyzer,
} from '../../modules/internationalization/intSelectors';
import type { IPageExpensesAnalyzerTranslation } from '../../modules/internationalization/types';
import type ICommonTranslation from '../../modules/internationalization/types/common';
import { ECommonTranslationPages } from '../../modules/internationalization/types/common/pages';
import { logBusinessEvent } from '../../modules/logging/loggingActions';
import type { ILogBusinessEventPayload } from '../../modules/logging/loggingTypes';
import { makeSelectIsColumnResizeEnabled } from '../../modules/role/selectors/guardSelector';
import { setCookie } from '../../modules/storage/storageActions';
import { makeSelectCookies, makeSelectIsDensity } from '../../modules/storage/storageSelectors';
import { ECookieKey } from '../../modules/storage/storageTypes';
import type { ICookies, ISetCookiePayload } from '../../modules/storage/storageTypes';
import type { IRootReducer } from '../../reducers/rootReducer';
import type { IRouteParams } from '../../routes/constants/routesConstants';
import ExpensesAnalyzerController from './components/ExpensesAnalyzerController/ExpensesAnalyzerController';
import { EExpensesAnalyzerChart } from './components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import ExpensesAnalyzerLayout from './components/ExpensesAnalyzerLayout';
import ExpensesAnalyzerTable from './components/ExpensesAnalyzerTable/ExpensesAnalyzerTable';
import { MapExpensesAnalyzerTypeToApiPath } from './components/ExpensesAnalyzerTable/types/expensesAnalyzerTableMapTypes';
import type { IExpensesAnalyzerApiPath } from './components/ExpensesAnalyzerTable/types/expensesAnalyzerTableMapTypes';
import ExpensesAnalyzerTitle from './components/ExpensesAnalyzerTitle/ExpensesAnalyzerTitle';
import {
    getExpensesAccountsListAction,
    initializeChartExpensesAnalyzerAction,
    loadChartsExpensesAnalyzerAction,
    setCurrentAccountExpensesAnalyzerAction,
    setCurrentChartExpensesAnalyzerAction,
    setExpensesAnalyzerType,
    setSelectedIdExpensesAnalyzerAction,
} from './pageExpensesAnalyzerActions';
import {
    makeSelectorExpensesAnalyzerCategoryData,
    makeSelectorExpensesAnalyzerCurrentAccount,
    makeSelectorExpensesAnalyzerCurrentChart,
    makeSelectorExpensesAnalyzerSelectedId,
    makeSelectorExpensesAnalyzerShowGraph,
    makeSelectorExpensesAnalyzerType,
} from './pageExpensesAnalyzerSelectors';
import { EExpensesAnalyzerTypes } from './types/pageExpensesAnalyzerTypes';
import type { IExpensesAccountsListItem, IExpensesAnalyzerMeta } from './types/pageExpensesAnalyzerTypes';

export interface IPageExpensesAnalyzerProps extends IExpensesAnalyzerMeta {
    type: EExpensesAnalyzerTypes;
}

export interface IMatchProps {
    clientId: string;
}

export type TPageExpensesAnalyzer = IDispatchProps &
    IConnectedProps &
    IPageExpensesAnalyzerProps &
    RouteComponentProps<IMatchProps>;

export const PageExpensesAnalyzer: FC<TPageExpensesAnalyzer> = (props: TPageExpensesAnalyzer) => {
    const { isShowGraph, isTableResizeEnabled, commonTranslation, expensesAnalyzerTranslation } = props;
    const [chartRenderState, setChartRenderState] = useState<boolean>(true);
    const [selectedId, setSelectedId] = useState<number>(SIDE_BAR_SELECT_ALL_ITEM);
    const [initialPageRender, setInitialPageRender] = useState<boolean>(true);
    const [sideBarNavMarginBottom, setSideBarNavMarginBottom] = useState<number>(85);

    const { clientId } = useParams<IRouteParams>(); // get id from url

    const query = new URLSearchParams(props.location?.search);
    const queryStringDate = query.get('d') || '';

    const applyRangeFilter = (): boolean => {
        try {
            if (queryStringDate) {
                props.setRangeMonthFilter({
                    filter: {
                        filterValue: queryStringDate,
                        valueFrom: queryStringDate,
                        valueTo: queryStringDate,
                    },
                    skipLoadList: true,
                    tableName: TableNameType.expensesAnalyzer,
                });
                return true;
            }
        } catch (err) {
            console.error(err);
        }
        return false;
    };

    useEffect(() => {
        setInitialPageRender(false);

        if (clientId) {
            props.setCurrentAccount(parseInt(clientId));
        } else {
            props.setCurrentAccount(SIDE_BAR_SELECT_ALL_ITEM);
        }

        setInitialConfiguration();
        let waitResetBasket = false;
        const type = clientId ? props.type + clientId : props.type;
        if (props.cookie.EXPENSE_ANALYZER_PAGE !== type) {
            waitResetBasket = true;
            resetBasket();
        }
        const waitFilterAndSort = applyRangeFilter();
        props.loadTableMeta({
            tableName: TableNameType.expensesAnalyzer,
            waitFilterAndSort,
            waitResetBasket,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialPageRender) return;
        const tablePathConfig = MapExpensesAnalyzerTypeToApiPath[props.type];
        setTableApiPathAllItems();
        const params: any = {
            resetBasket: false,
            resetFilter: false,
            tableName: TableNameType.expensesAnalyzer,
            type: EResetBasketType.CLEAR_META,
        };
        if (selectedId === SIDE_BAR_SELECT_ALL_ITEM && tablePathConfig.all) {
            params.resetState = false;
        } else {
            params.id = selectedId;
        }
        props.resetBasket(params);
        props.loadTableMeta({
            tableName: TableNameType.expensesAnalyzer,
            waitResetBasket: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const getApiPathConfiguration = (initial = false): IExpensesAnalyzerApiPath => {
        const id = props.selectedId || selectedId;
        if (initial || !id || props.selectedId === SIDE_BAR_SELECT_ALL_ITEM) {
            return (
                MapExpensesAnalyzerTypeToApiPath[props.type].all || MapExpensesAnalyzerTypeToApiPath[props.type].common
            );
        } else {
            return MapExpensesAnalyzerTypeToApiPath[props.type].common;
        }
    };

    const setInitialConfiguration = () => {
        props.setSelectedId(SIDE_BAR_SELECT_ALL_ITEM);
        props.setExpensesAnalyzerType(props.type);
        props.setCurrentChart(EExpensesAnalyzerChart.CATEGORY);
        setTableApiPathAllItems(true);
        props.initializeChart();
        props.getExpensesAccountsList(true);
    };

    /*
     * -------------------------------------------
     *           CONTROLLER
     * -------------------------------------------
     */
    const sideBarWidth = props.isTableAccount ? 200 : 0;

    const selectAccountHandler = (account: IExpensesAccountsListItem): void => {
        props.setSelectedId(account.clntId);
        setSelectedId(account.clntId);
        props.resetBasketCount();
    };

    const resetBasket = () => {
        props.resetBasket({
            id: clientId,
            resetBasket: true,
            resetFilter:
                props.type !== EExpensesAnalyzerTypes.AccountSubscribers &&
                props.type !== EExpensesAnalyzerTypes.AccountSingleSubscribers,
            tableName: TableNameType.expensesAnalyzer,
            type: EResetBasketType.CLEAR_META,
        });
        props.setCookie({
            key: ECookieKey.EXPENSE_ANALYZER_PAGE,
            value: clientId ? props.type + clientId : props.type,
        });
    };

    /*
     * -------------------------------------------
     *           CHART
     * -------------------------------------------
     */

    const loadChart = () => {
        props.loadChart();
    };

    /*
     * -------------------------------------------
     *           TABLE
     * -------------------------------------------
     */

    const setTableApiPathAllItems = (initial = false) => {
        const apiPathConfiguration = getApiPathConfiguration(initial);

        MapTableNameToApiPath.expensesAnalyzer = apiPathConfiguration.api;
        MapTableNameToBasketApiPath.expensesAnalyzer = apiPathConfiguration.basket;
        MapTableNameToListApiPath.expensesAnalyzer = apiPathConfiguration.list;
        MapTableNameToProxyApiPath.expensesAnalyzer = apiPathConfiguration.list;
        MapTableNameToDetailsApiPath.expensesAnalyzer = apiPathConfiguration.tableDetails;
    };

    const sendTableFields = (fields: ITableReducer[TableNameType]['fields']): void => {
        props.sendTableFields({
            fields: fields,
            tableName: TableNameType.expensesAnalyzer,
        });
    };

    const getExportPath = () => {
        let exportPath = getApiPathConfiguration().export;
        const queryParams = [];

        // id
        if (clientId) {
            queryParams.push(`id=${clientId}`);
        } else if (props.selectedId) {
            queryParams.push(`id=${props.selectedId}`);
        }

        // with graph
        if (props.isShowGraph) {
            queryParams.push(`chart=${props.currentChart.toUpperCase()}`);
        }

        // with category
        props.category
            .filter((category: IChartDataInput) => category[EChartDataKeyEnum.checked])
            .forEach((category: IChartDataInput) => {
                queryParams.push(`categories=${category[EChartDataKeyEnum.x]}`);
            });

        exportPath = `${exportPath}?${queryParams.join('&')}`;
        return exportPath;
    };

    const loadTableList = (): void => {
        props.loadTableList();
    };

    const saveResizedChange = (fields: IFieldResize[]) => {
        props.sendTableFieldsSize({
            fields: fields.map((field) => ({ name: field.id, size: field.value })),
            tableName: TableNameType.expensesAnalyzer,
        });
    };

    const handleChangeFooterSize = (footerSize: ITableFooterSize) => {
        setSideBarNavMarginBottom(footerSize.footerHeight - footerSize.horScrollHeight);
    };

    /*
     * -------------------------------------------
     *           RENDER
     * -------------------------------------------
     */
    return (
        <PageLocale
            fileNames={[
                TranslationFileNamesEnum.common,
                TranslationFileNamesEnum.corporate,
                TranslationFileNamesEnum.pageExpensesAnalyzer,
            ]}
        >
            <Title pageName={ECommonTranslationPages.expensesAnalyzer} />

            <ExpensesAnalyzerTitle exportPath={getExportPath()} sendTableFields={sendTableFields} />

            <ExpensesAnalyzerController
                chartRenderCallback={setChartRenderState}
                isDynamicEnabled={!props.isDynamicDisabled}
                isShowChart={isShowGraph}
            />

            <ExpensesAnalyzerLayout
                getExpensesAccountsList={props.getExpensesAccountsList}
                isTableAccount={props.isTableAccount || false}
                selectAccountHandler={selectAccountHandler}
                selectedId={props.selectedId}
                sideBarMarginBottom={sideBarNavMarginBottom}
                translation={expensesAnalyzerTranslation}
            >
                <ExpensesAnalyzerTable
                    forceFooterUpdate={chartRenderState}
                    isDensity={props.isDensity}
                    isTableResizeEnabled={isTableResizeEnabled}
                    loadChart={loadChart}
                    loadTableList={loadTableList}
                    loadTableSubRow={props.loadTableSubRow}
                    saveResizedChange={saveResizedChange}
                    sendTableFields={sendTableFields}
                    setTableSort={props.setTableSort}
                    sideBarWidth={sideBarWidth}
                    switchCheckbox={props.switchCheckbox}
                    tableCheckedItems={props.tableCheckedItems}
                    translation={{
                        ...commonTranslation,
                        ...expensesAnalyzerTranslation,
                    }}
                    type={props.type}
                    onChangeFooterSize={handleChangeFooterSize}
                />
            </ExpensesAnalyzerLayout>
        </PageLocale>
    );
};

export interface IConnectedProps {
    category: IChartDataInput[];
    commonTranslation: ICommonTranslation;
    cookie: ICookies;
    currentChart: EExpensesAnalyzerChart;
    currentTableName: TableNameType;
    entityId: number; // bisAccountId or subsid
    expensesAnalyzerTranslation: IPageExpensesAnalyzerTranslation;
    expensesAnalyzerType: EExpensesAnalyzerTypes;
    isDensity: boolean;
    isShowGraph: boolean;
    isTableResizeEnabled: boolean;
    selectedFilters: ITableReducer[TableNameType]['filter'];
    selectedId: number;
    tableCheckedItems: IBasketItem[];
    tableData: ITableReducer[TableNameType]['data'];
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    category: makeSelectorExpensesAnalyzerCategoryData(),
    commonTranslation: makeSelectTranslationCommon(),
    cookie: makeSelectCookies(),
    currentChart: makeSelectorExpensesAnalyzerCurrentChart(),
    currentTableName: makeSelectorTableCurrentName(),
    entityId: makeSelectorExpensesAnalyzerCurrentAccount(),
    expensesAnalyzerTranslation: makeSelectTranslationPageAnalyzer(),
    expensesAnalyzerType: makeSelectorExpensesAnalyzerType(),
    isDensity: makeSelectIsDensity(),
    isShowGraph: makeSelectorExpensesAnalyzerShowGraph(),
    isTableResizeEnabled: makeSelectIsColumnResizeEnabled(),
    selectedFilters: makeSelectorTableFilter(),
    selectedId: makeSelectorExpensesAnalyzerSelectedId(),
    tableCheckedItems: makeSelectorInitBasketList(),
    tableData: makeSelectorTableData(TableNameType.expensesAnalyzer),
});

export interface IDispatchProps {
    resetBasket: (payload: IResetBasketActionPayload) => void;
    getExpensesAccountsList: (payload?: boolean) => void;
    initializeChart: () => void;
    loadChart: () => void;
    loadTableMeta: (payload: ILoadTableMetaPayload) => void;
    loadTableList: (payload?: ILoadTableListPayload) => void;
    loadTableSubRow: (payload: number) => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
    resetBasketCount: () => void;
    sendTableFields: (payload: ISendTableFieldsPayload) => void;
    sendTableFieldsSize: (payload: ISendTableFieldsSizePayload) => void;
    setCookie: (payload: ISetCookiePayload) => void;
    setCurrentAccount: (payload: number) => void;
    setCurrentChart: (payload: EExpensesAnalyzerChart) => void;
    setSelectedId: (payload: number) => void;
    setExpensesAnalyzerType: (payload: EExpensesAnalyzerTypes) => void;
    setRangeMonthFilter: (payload: ISetRangeMonthFilterPayload) => void;
    setTableCurrentName: (payload: TableNameType) => void;
    setTableSort: (payload: ISetTableSortPayload) => void;
    switchCheckbox: (payload: ISwitchCheckboxPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    resetBasket: (payload) => dispatch(resetBasketAction(payload)),
    getExpensesAccountsList: (payload) => dispatch(getExpensesAccountsListAction(payload)),
    initializeChart: () => dispatch(initializeChartExpensesAnalyzerAction()),
    loadChart: () => dispatch(loadChartsExpensesAnalyzerAction()),
    loadTableMeta: (payload) => dispatch(loadTableMeta(payload)),
    loadTableList: (payload) => dispatch(loadTableListAction(payload)),
    loadTableSubRow: (payload) => dispatch(loadTableSubRow({ subRowId: payload })),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
    resetBasketCount: () => dispatch(resetBasketCount({ tableName: TableNameType.expensesAnalyzer })),
    sendTableFields: (payload) => dispatch(sendTableFields(payload)),
    sendTableFieldsSize: (payload) => dispatch(sendTableFieldsSize(payload)),
    setCookie: (payload) => dispatch(setCookie(payload)),
    setCurrentAccount: (payload) => dispatch(setCurrentAccountExpensesAnalyzerAction(payload)),
    setCurrentChart: (payload) => dispatch(setCurrentChartExpensesAnalyzerAction(payload)),
    setSelectedId: (payload) => dispatch(setSelectedIdExpensesAnalyzerAction(payload)),
    setExpensesAnalyzerType: (payload) => dispatch(setExpensesAnalyzerType(payload)),
    setRangeMonthFilter: (payload) => dispatch(setRangeMonthFilter(payload)),
    setTableCurrentName: (payload) => dispatch(setTableCurrentName(payload)),
    setTableSort: (payload) => dispatch(setTableSortAction(payload)),
    switchCheckbox: (payload) => dispatch(switchCheckbox(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(PageExpensesAnalyzer);
